// Revenue Console — Palantir-style dark/light console for the Revenue area.
// Replaces the stacked RevenueHub (Revenue + Subscriptions + Refund Queue) with
// one dense console. All figures are live: useRevenueStats (access_events),
// usePassesStats (user_passes), and a direct MONTHLY-subscription roll-up.
import React, { useState, useEffect } from 'react';
import {
  ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Pill, Table, Drawer,
  useConsoleTheme, useIsMobile,
} from '../components/Console';
import { useRevenueStats, usePassesStats } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { setFocus } from '../utils/consoleBus';
import { MONTHLY_PRICE } from '../constants/pricing';

// Payment failures — admin_payment_failures (attempts, pay diagnostics, capture failures)
function usePayFailures() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let x = false;
    const load = () => supabase.rpc('admin_payment_failures', { p_limit: 60 }).then(({ data }) => { if (!x) setD(data || []); });
    load();
    const iv = setInterval(load, 120000);
    return () => { x = true; clearInterval(iv); };
  }, []);
  return d;
}

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const fmtUSD = n => (n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

function useSubscriptionRollup() {
  const [state, setState] = useState({ loading: true, subs: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('admin_subscription_rollup');
        const now = new Date();
        const map = {};
        (data || []).forEach(r => {
          const key = r.stripe_subscription_id || ('user:' + (r.user_id || 'unknown'));
          if (!map[key]) map[key] = { key, user_id: r.user_id, first: null, expires: null, cancelled_at: null, refunded_at: null, cancel_reason: null, periods: 0, monthsPaid: 0 };
          const s = map[key]; s.periods++;
          const start = r.created_at ? new Date(r.created_at) : null;
          const end = r.expires_at ? new Date(r.expires_at) : null;
          if (start && (!s.first || start < s.first)) s.first = start;
          if (end && (!s.expires || end > s.expires)) s.expires = end;
          if (r.cancelled_at) { const c = new Date(r.cancelled_at); if (!s.cancelled_at || c > s.cancelled_at) { s.cancelled_at = c; s.cancel_reason = r.cancel_reason || s.cancel_reason; } }
          if (r.refunded_at) { const rf = new Date(r.refunded_at); if (!s.refunded_at || rf > s.refunded_at) s.refunded_at = rf; }
          if (start) { const endForCalc = end && end < now ? end : now; s.monthsPaid += Math.max(0, (endForCalc - start) / (30 * 86400000)); }
        });
        if (!cancelled) setState({ loading: false, subs: Object.values(map) });
      } catch (_e) { if (!cancelled) setState({ loading: false, subs: [] }); }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

function useRefundQueue() {
  const [d, setD] = useState({ loading: true, total: 0, pending: 0 });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('refund_requests').select('status').limit(2000);
        const total = (data || []).length;
        const pending = (data || []).filter(r => ['pending', 'requested', 'open', 'new'].includes((r.status || '').toLowerCase())).length;
        if (!cancelled) setD({ loading: false, total, pending });
      } catch (_e) { if (!cancelled) setD({ loading: false, total: 0, pending: 0 }); }
    })();
    return () => { cancelled = true; };
  }, []);
  return d;
}

function RevUserInspector({ userId }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_user_detail', { p_user_id: userId }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [userId]);
  const day = x => x ? new Date(x).toISOString().slice(0, 10) : '—';
  if (!d) return <Empty>Loading subscriber…</Empty>;
  return (
    <div>
      <div style={{ fontSize:'1rem', fontWeight:500, color:t.tx, marginBottom:2 }}>{d.email || '—'}</div>
      <div style={{ fontSize:'0.78rem', color:t.mut, fontFamily:'monospace', marginBottom:12 }}>{(userId || '').slice(0, 18)}…</div>
      <StatLine label="Plan" value={d.active_plan || 'free'} color={d.active_plan ? t.up : t.mut} />
      {d.active_plan && <StatLine label="Renews / expires" value={day(d.active_expires)} />}
      <StatLine label="Passes (all)" value={fmt((d.passes || []).length)} />
      <StatLine label="Sana queries" value={fmt(d.sana_invocations || 0)} />
      <StatLine label="Joined" value={day(d.created_at)} />
      <StatLine label="Flagged" value={d.flagged ? 'yes' : 'no'} color={d.flagged ? t.danger : t.up} />
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'0.74rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Pass history</div>
      {(d.passes || []).length === 0 ? <Empty>No passes.</Empty> : (d.passes || []).slice(0, 10).map((p, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', padding:'4px 0', borderBottom:`.5px solid ${t.bd}` }}>
          <span style={{ color:t.tx }}>{p.pass_type}</span>
          <span style={{ color:t.mut }}>{day(p.created_at)} → {day(p.expires_at)}</span>
        </div>
      ))}
    </div>
  );
}

function RevenueBody({ onNav }) {
  const { t } = useConsoleTheme();
  const payFails = usePayFailures();
  const mobile = useIsMobile();
  const [selUser, setSelUser] = useState(null);
  const { data: rev } = useRevenueStats();
  const passes = usePassesStats().data;
  const { loading: subL, subs } = useSubscriptionRollup();
  const refunds = useRefundQueue();

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const isActive = s => !s.cancelled_at && !s.refunded_at && s.expires && s.expires > now;
  const activeSubs = subs.filter(isActive);
  const mrr = activeSubs.length * MONTHLY_PRICE;
  const tenureMonths = activeSubs.length ? (activeSubs.reduce((a, s) => a + (now - s.first) / (30 * 86400000), 0) / activeSubs.length) : 0;
  const ltv = tenureMonths * MONTHLY_PRICE;
  const cancelledThisMonth = subs.filter(s => s.cancelled_at && s.cancelled_at >= startOfMonth);
  const activeAtMonthStart = subs.filter(s => s.first && s.first < startOfMonth && !(s.cancelled_at && s.cancelled_at < startOfMonth) && !(s.refunded_at && s.refunded_at < startOfMonth));
  const churn = activeAtMonthStart.length ? Math.round((cancelledThisMonth.length / activeAtMonthStart.length) * 1000) / 10 : 0;

  const buckets = [
    { label: '0–1 month', min: 0, max: 1, c: '#dc3545' },
    { label: '1–3 months', min: 1, max: 3, c: '#fd7e14' },
    { label: '3–6 months', min: 3, max: 6, c: '#9F7E4F' },
    { label: '6–12 months', min: 6, max: 12, c: '#5a8fa6' },
    { label: '12+ months', min: 12, max: Infinity, c: '#28a745' },
  ].map(b => ({ ...b, count: subs.filter(s => s.monthsPaid >= b.min && s.monthsPaid < b.max).length }));
  const maxBucket = Math.max(1, ...buckets.map(b => b.count));

  const reasonCounts = {};
  cancelledThisMonth.forEach(s => { const r = s.cancel_reason || 'Unknown'; reasonCounts[r] = (reasonCounts[r] || 0) + 1; });
  const reasonRows = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
  const maxReason = Math.max(1, ...reasonRows.map(([, c]) => c));

  const byType = (rev && rev.byType) || [];
  const maxSku = Math.max(1, ...byType.map(b => b.revenue || 0));
  const hasRevenue = rev && rev.totalRev > 0;
  const recent = (rev && rev.recent) || [];

  const col = (n) => mobile ? (n >= 4 ? '1fr 1fr' : '1fr') : `repeat(${n},1fr)`;

  const roster = subs
    .map(s => ({ ...s, paid: s.monthsPaid * MONTHLY_PRICE, active: isActive(s) }))
    .sort((a, b) => (b.active - a.active) || (b.monthsPaid - a.monthsPaid))
    .slice(0, 25);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(6), gap:10 }}>
        <KPI label="Net revenue · all-time" value={fmtUSD(rev ? rev.totalRev : 0)} sub={hasRevenue ? `${fmt(rev.total)} purchases` : 'awaiting launch'} subColor={hasRevenue?t.up:t.mut} accent="#17a86a" />
        <KPI label="Net · 30d" value={fmtUSD(rev ? rev.rev30 : 0)} sub="last 30 days" accent={t.accent} />
        <KPI label="MRR" value={fmtUSD(mrr)} sub={`${fmt(activeSubs.length)} active subs`} accent="#314F5C" />
        <KPI label="ARPU" value={rev ? `$${rev.arpu}` : '—'} sub="per paying user" accent="#6F8FA7" />
        <KPI label="Churn · month" value={`${churn}%`} sub={`${fmt(cancelledThisMonth.length)} cancelled`} subColor={churn>=25?t.danger:churn>=10?t.warn:t.up} accent={churn>=25?'#dc3545':churn>=10?'#fd7e14':'#28a745'} />
        <KPI label="Refund requests" value={fmt(refunds.total)} sub={`${fmt(refunds.pending)} pending`} subColor={refunds.pending>0?t.warn:t.mut} accent="#9F7E4F" />
      </div>

      <SectionLabel right={hasRevenue ? undefined : 'pre-launch'}>Revenue breakdown</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile?'1fr':'1fr 1fr', gap:12 }}>
        <Panel title="◇ Revenue by SKU">
          {byType.length === 0
            ? <Empty>No purchases recorded yet. Revenue fills in once the first <code style={{ background:t.codebg, color:t.accent, padding:'1px 4px', borderRadius:4, fontFamily:'monospace' }}>access_event</code> lands.</Empty>
            : byType.map(b => <Bar key={b.type} label={b.label || b.type} value={fmtUSD(b.revenue)} pct={Math.round((b.revenue/maxSku)*100)} labelWidth={110} />)}
        </Panel>
        <Panel title="◈ Subscription health" right="MONTHLY">
          <StatLine label="Active subscribers" value={subL ? '…' : fmt(activeSubs.length)} />
          <StatLine label={"MRR (active × $" + MONTHLY_PRICE + " list)"} value={fmtUSD(mrr)} />
          <StatLine label="Avg tenure" value={`${tenureMonths.toFixed(1)} mo`} />
          <StatLine label="Projected LTV" value={fmtUSD(ltv)} color={t.up} />
          <StatLine label="Lifetime passes sold" value={fmt(passes.total)} />
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          <div style={{ fontSize:'.66rem', color:t.mut, marginBottom:8, letterSpacing:'.06em', textTransform:'uppercase' }}>Tenure buckets</div>
          {subs.length === 0
            ? <Empty>No MONTHLY subscriptions yet — bucket data appears after the first paid signup.</Empty>
            : buckets.map(b => <Bar key={b.label} label={b.label} value={fmt(b.count)} pct={Math.round((b.count/maxBucket)*100)} color={b.c} labelWidth={96} />)}
        </Panel>
      </div>

      <SectionLabel right={payFails ? `${payFails.filter(f => !f.resolved).length} open` : undefined}>Payment failures</SectionLabel>
      <Panel style={{ marginBottom: 4 }}>
        {!payFails ? <Empty>Loading payment diagnostics…</Empty> : payFails.length === 0 ? (
          <Empty>No failed payments, diagnostics or capture failures recorded. This feed covers checkout attempts, pay endpoint diagnostics and Duffel capture failures.</Empty>
        ) : (
          <div>
            {payFails.slice(0, 12).map((f, i) => (
              <div key={i} style={{ display:'flex', gap:10, padding:'7px 0', borderBottom:`1px solid ${t.bd}`, fontSize:'0.84rem', alignItems:'baseline' }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background: f.resolved ? t.up : t.danger, flexShrink:0, alignSelf:'center' }} />
                <span style={{ color:t.mut, whiteSpace:'nowrap', fontFamily:'monospace', fontSize:'0.72rem' }}>{(f.at || '').slice(0, 16).replace('T', ' ')}</span>
                <span style={{ color:t.gold, whiteSpace:'nowrap', fontSize:'0.76rem', fontWeight:600 }}>{f.kind}</span>
                <span style={{ color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }} title={f.detail}>{f.what}{f.detail ? ' — ' + f.detail : ''}</span>
              </div>
            ))}
            {payFails.length > 12 && <div style={{ fontSize:'0.74rem', color:t.mut, marginTop:8 }}>{payFails.length - 12} older entries.</div>}
          </div>
        )}
      </Panel>

      <SectionLabel>Refunds & cancellations</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile?'1fr':'1fr 1fr', gap:12 }}>
        <Panel title="◷ Refund queue">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:4 }}>
            <KPI label="Requests" value={fmt(refunds.total)} />
            <KPI label="Pending" value={fmt(refunds.pending)} subColor={t.warn} accent={refunds.pending>0?'#d97706':undefined} />
            <KPI label="Refunded passes" value={fmt(rev ? rev.refunds : 0)} />
          </div>
          <div style={{ marginTop:10 }}>
            <Empty>Only ANNUAL has a 14-day refund window; MONTHLY is final. Refunds are issued in Google Play Console. Flagged / repeat-refund / locked accounts are denied server-side.</Empty>
          </div>
        </Panel>
        <Panel title="◔ Cancel reasons" right="this month">
          {reasonRows.length === 0
            ? <Empty>No cancellations this month.</Empty>
            : reasonRows.map(([r, c]) => <Bar key={r} label={r} value={fmt(c)} pct={Math.round((c/maxReason)*100)} color={t.danger} labelWidth={150} />)}
        </Panel>
      </div>

      <SectionLabel right={`${fmt(subs.length)} subs · ${fmt(recent.length)} recent`}>Ledger</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile?'1fr':'1fr 1fr', gap:12 }}>
        <Panel title="◇ Recent purchases">
          <Table
            empty={hasRevenue ? 'No recent purchases.' : 'No purchases yet — the ledger fills at launch.'}
            cols={[
              { label:'Date', key:'d', muted:true },
              { label:'Plan', key:'p' },
              { label:'Price', key:'pr', align:'right', num:true },
              { label:'Email', key:'e', muted:true },
            ]}
            rows={recent.slice(0, 12).map(r => ({
              d: r.created_at ? new Date(r.created_at).toISOString().slice(0,10) : '—',
              p: r.pass_label || r.pass_type || '—',
              pr: fmtUSD(r.price_usd),
              e: r.email ? (r.email.length > 22 ? r.email.slice(0,20) + '…' : r.email) : '—',
            }))}
          />
        </Panel>
        <Panel title="◈ Subscriber roster" right={`${fmt(subs.length)}`}>
          {roster.length === 0
            ? <Empty>No MONTHLY subscribers in the last 1,000 pass rows.</Empty>
            : <Table
                onRowClick={r => r.user_id && setSelUser(r.user_id)}
                cols={[
                  { label:'User', render:r => (r.user_id ? r.user_id.slice(0,8)+'…' : '—'), mono:true, muted:true },
                  { label:'Status', render:r => {
                      const st = r.refunded_at ? 'refunded' : r.cancelled_at ? 'cancelled' : r.active ? 'active' : 'lapsed';
                      const tone = st==='active'?'up':st==='refunded'?'warn':'danger';
                      return <Pill tone={tone}>{st}</Pill>;
                    } },
                  { label:'Mo paid', render:r => r.monthsPaid.toFixed(1), align:'right', num:true },
                  { label:'LTV', render:r => fmtUSD(r.paid), align:'right', num:true },
                ]}
                rows={roster}
              />}
        </Panel>

        <Drawer open={!!selUser} onClose={() => setSelUser(null)} title="Subscriber">
          {selUser && <RevUserInspector userId={selUser} />}
          {selUser && <div style={{ marginTop:16 }}><button onClick={() => { setFocus({ type:'user', value:selUser }); onNav && onNav('users'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Open user →</button></div>}
        </Drawer>
      </div>
    </div>
  );
}

export default function RevenueConsole(props) {
  return (
    <ConsoleShell title="Revenue Console">
      <RevenueBody onNav={props.onNav} />
    </ConsoleShell>
  );
}
